/**
 * @voltxampere/ui — Wire renderer (Sprint 12 → Sprint 52, ADR-0085).
 *
 * Sprint 12 shipped straight-segment wires only. Sprint 13 added the
 * Manhattan auto-router (multi-point orthogonal `path`). Sprint 52
 * (this revision) adds catenary sag — a one-segment cubic Bezier for
 * two-point wires when the `wireCurve` style is `"catenary"`.
 *
 * Style dispatch contract:
 *
 * | Style          | Path length | Render call                |
 * | -------------- | ----------- | -------------------------- |
 * | `catenary`     | `2`         | `bezierCurveTo` (Sprint 52)|
 * | `catenary`     | `3+`        | polyline fallback          |
 * | `manhattan`    | any         | polyline (Sprint 13)       |
 * | `linear` / unset | any       | polyline (Sprint 12)       |
 *
 * Multi-point catenary fallback: a wire that hits a junction node
 * (`path.length ≥ 3`) keeps the orthogonal Manhattan rendering even
 * when the user picks `catenary` — sag between every pin pair would
 * make junctions visually ambiguous.
 *
 * `lineCap = 'square'` matches the Zenit §9.1 stroke contract —
 * round endcaps are forbidden by §12 antipatterns.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { DEFAULT_WIRE_SLACK } from "@voltxampere/schema";
import { computeCatenary, sampleCatenary } from "./catenary-math.js";
/**
 * Sprint 52 ADR-0085 — gauge → stroke-width mapping.
 *
 * The four documented AWG values map to the same `theme.stroke.default`
 * (2 px) baseline scaled by 0.75 / 1.25 / 1.5 to give the user a one-
 * dropdown sense of wire weight. The 24AWG default matches what wires
 * shipped with through Sprint 51, so wires that omit the field render
 * identically to before this sprint.
 */
const GAUGE_THICKNESS = Object.freeze({
    "20AWG": 3,
    "22AWG": 2.5,
    "24AWG": 2,
    "28AWG": 1.5,
});
function gaugeToThickness(gauge, fallback) {
    if (gauge === undefined)
        return fallback;
    // `Record<WireGauge, number>` (frozen) guarantees a number, but
    // routing through `Object.freeze` widens to a readonly index map;
    // keep the explicit narrow so a future enum addition fails loud.
    switch (gauge) {
        case "20AWG":
        case "22AWG":
        case "24AWG":
        case "28AWG":
            return GAUGE_THICKNESS[gauge];
    }
}
export function drawWire(ctx, wire, theme, selected, wireCurve = "linear") {
    if (wire.path.length < 2)
        return;
    ctx.save();
    ctx.strokeStyle = selected ? theme.accent.brassHot : theme.text.dim;
    // Sprint 52 ADR-0085 — gauge dispatches the unselected width.
    // Selected wires keep the theme override so the brass-hot accent
    // reads consistently across all gauges.
    ctx.lineWidth = selected
        ? theme.stroke.selected
        : gaugeToThickness(wire.gauge, theme.stroke.default);
    ctx.lineCap = "square";
    ctx.lineJoin = "miter";
    // Sprint 52 — catenary single-segment (2-pin) wire.
    if (wireCurve === "catenary" && wire.path.length === 2) {
        const p1 = wire.path[0];
        const p2 = wire.path[1];
        if (p1 === undefined || p2 === undefined) {
            ctx.restore();
            return;
        }
        const slack = wire.slack ?? DEFAULT_WIRE_SLACK;
        const catenary = computeCatenary({ p1, p2, slack });
        ctx.beginPath();
        if (catenary.sag === 0 || ctx.bezierCurveTo === undefined) {
            // Fallback path — short / vertical / taut wire, or a context
            // mock that omits the optional `bezierCurveTo` slot.
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
        }
        else {
            // Reconstruct the two control points from the same closed-form
            // math `computeCatenary` uses, so the canvas curve is bit-exact
            // with the path-string consumers (hit-test, bbox).
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            const sagDepth = catenary.sag * (4 / 3);
            const c1x = p1.x + dx / 3;
            const c1y = p1.y + dy / 3 + sagDepth;
            const c2x = p2.x - dx / 3;
            const c2y = p2.y - dy / 3 + sagDepth;
            ctx.moveTo(p1.x, p1.y);
            ctx.bezierCurveTo(c1x, c1y, c2x, c2y, p2.x, p2.y);
        }
        ctx.stroke();
        // Cache hit-test samples for the quadtree.
        wire._catenarySamples = sampleCatenary({ p1, p2, slack }, 16);
        ctx.restore();
        return;
    }
    // Manhattan / linear / catenary-with-junctions — polyline fallback.
    ctx.beginPath();
    const head = wire.path[0];
    if (head === undefined) {
        ctx.restore();
        return;
    }
    ctx.moveTo(head.x, head.y);
    for (let i = 1; i < wire.path.length; i++) {
        const p = wire.path[i];
        if (p === undefined)
            continue;
        ctx.lineTo(p.x, p.y);
    }
    ctx.stroke();
    ctx.restore();
}
//# sourceMappingURL=wire.js.map